/**
 * Process POS Payment Actor
 *
 * Polls Stripe Terminal Reader for payment status using processPaymentIntent.
 * Monitors reader action status until payment completes (succeeded/failed).
 */

import { datadogLogs } from "@datadog/browser-logs";
import {
  getTerminalByReaderId,
  processPOSPaymentByReaderId,
  processPOSSetupIntentByReaderId,
} from "@frontend/lib/actions/stripe-actions";
import type Stripe from "stripe";
import { fromPromise } from "xstate";
import { z } from "zod";
import { getKioskId } from "../../lib/kiosk-identity";
import { trackNetworkRequest } from "../../lib/network-diagnostics";

/**
 * Input Schema (What this actor needs)
 */
const ProcessPOSPaymentInputSchema = z
  .object({
    paymentIntentId: z.string().optional(),
    setupIntentId: z.string().optional(),
    readerId: z.string(),
    consumerId: z.string().optional(),
    pollInterval: z.number().optional().default(1000), // Poll every 1s by default
    timeout: z.number().optional().default(120000), // 2 minute timeout
  })
  .refine(
    (data) => data.paymentIntentId || data.setupIntentId,
    "Either paymentIntentId or setupIntentId must be provided",
  );

type ProcessPOSPaymentInput = z.infer<typeof ProcessPOSPaymentInputSchema>;

/**
 * Success Schema (What this actor returns on success)
 */
const ProcessPOSPaymentSuccessSchema = z.object({
  status: z.literal("succeeded"),
  paymentIntentId: z.string().optional(),
  setupIntentId: z.string().optional(),
  reader: z.any(), // Stripe.Terminal.Reader
});

type ProcessPOSPaymentSuccess = z.infer<typeof ProcessPOSPaymentSuccessSchema>;

/**
 * Error Schema (What this actor returns on failure)
 */
const ProcessPOSPaymentErrorSchema = z.object({
  code: z.enum([
    "INVALID_INPUT",
    "READER_OFFLINE",
    "READER_NOT_FOUND",
    "PAYMENT_FAILED",
    "PAYMENT_CANCELED",
    "TIMEOUT",
    "NETWORK_ERROR",
    "UNKNOWN_ERROR",
  ]),
  message: z.string(),
  retryable: z.boolean(),
});

type ProcessPOSPaymentError = z.infer<typeof ProcessPOSPaymentErrorSchema>;

/**
 * Actor metadata
 */
export interface ActorMetadata {
  displayName: string;
  description: string;
  category: "communication" | "data" | "auth" | "unknown";
  retryable: boolean;
  timeout: number;
  errorCodes: readonly string[];
  inputSchema: z.ZodSchema;
  successSchema: z.ZodSchema;
  errorSchema: z.ZodSchema;
  onSuccess: (result: unknown) => Record<string, unknown>;
  onError: (error: unknown) => Record<string, unknown>;
  composedActors?: readonly string[];
}

/**
 * Actor with metadata wrapper
 */
export interface ActorWithMetadata {
  // biome-ignore lint/suspicious/noExplicitAny: XState actor types are too complex for static typing
  impl: any;
  metadata: ActorMetadata;
}

/**
 * Helper: Poll reader status until payment completes
 */
async function pollReaderStatus(
  readerId: string,
  pollInterval: number,
  timeout: number,
): Promise<Stripe.Terminal.Reader> {
  const startTime = Date.now();

  while (Date.now() - startTime < timeout) {
    const stringifiedReader = await getTerminalByReaderId(readerId);

    if (!stringifiedReader) {
      const error = new Error(`Reader ${readerId} not found`);
      Object.assign(error, {
        code: "READER_NOT_FOUND",
        message: `Reader ${readerId} not found`,
        retryable: false,
        actorName: "processPOSPayment",
        functionName: "pollReaderStatus",
        location: "process-pos-payment.actor.ts:99",
      });
      throw error;
    }

    const reader = JSON.parse(stringifiedReader) as Stripe.Terminal.Reader;

    // NOTE: We intentionally DO NOT check reader.status here.
    // Stripe's documentation warns: "We do not recommend using this field in flows
    // that may block taking payments." The status field is unreliable and can
    // flicker between online/offline even when the device is functioning normally.
    // Instead, we only rely on reader.action.status for payment outcome.
    // See: https://docs.stripe.com/api/terminal/readers/object
    if (reader.status !== "online") {
      console.warn(
        `[pollReaderStatus] Reader ${readerId} status is "${reader.status}" but continuing - status field is unreliable`,
      );
    }

    // Check action status
    if (reader.action?.status === "succeeded") {
      return reader;
    }

    if (reader.action?.status === "failed") {
      const error = new Error(
        reader.action?.failure_message || "Payment failed on terminal reader",
      );
      Object.assign(error, {
        code: "PAYMENT_FAILED",
        message:
          reader.action?.failure_message || "Payment failed on terminal reader",
        retryable: true,
        actorName: "processPOSPayment",
        functionName: "pollReaderStatus",
        location: "process-pos-payment.actor.ts:134",
      });
      throw error;
    }

    // Action was canceled
    if (!reader.action) {
      const error = new Error("Payment was canceled on terminal reader");
      Object.assign(error, {
        code: "PAYMENT_CANCELED",
        message: "Payment was canceled on terminal reader",
        retryable: false,
        actorName: "processPOSPayment",
        functionName: "pollReaderStatus",
        location: "process-pos-payment.actor.ts:147",
      });
      throw error;
    }

    // Still in progress, wait and poll again
    await new Promise((resolve) => setTimeout(resolve, pollInterval));
  }

  // Timeout reached
  const error = new Error(`Payment polling timeout after ${timeout}ms`);
  Object.assign(error, {
    code: "TIMEOUT",
    message: `Payment polling timeout after ${timeout}ms`,
    retryable: true,
    actorName: "processPOSPayment",
    functionName: "pollReaderStatus",
    location: "process-pos-payment.actor.ts:168",
  });
  throw error;
}

/**
 * Process POS Payment Actor Implementation
 *
 * NOTE: XState compatibility - we unwrap neverthrow Results here:
 * - throw error → XState onError transition
 * - return value → XState onDone transition
 */
const processPOSPaymentActorImpl = fromPromise(
  async ({
    input,
  }: {
    input: ProcessPOSPaymentInput;
  }): Promise<ProcessPOSPaymentSuccess> => {
    const {
      paymentIntentId,
      setupIntentId,
      readerId,
      consumerId,
      pollInterval,
      timeout,
    } = input;

    // Validate input
    const validated = ProcessPOSPaymentInputSchema.safeParse({
      paymentIntentId,
      setupIntentId,
      readerId,
      pollInterval,
      timeout,
    });

    if (!validated.success) {
      const errorData: ProcessPOSPaymentError = {
        code: "INVALID_INPUT",
        message: `Validation failed: ${validated.error.message}`,
        retryable: false,
      };
      const error = new Error(errorData.message);
      Object.assign(error, {
        ...errorData,
        actorName: "processPOSPayment",
        location: "process-pos-payment.actor.ts:190",
      });
      throw error;
    }

    const tracker = trackNetworkRequest("processPOSPayment", {
      practiceId: readerId, // Using readerId as identifier for correlation
    });

    try {
      // Step 1: Initiate payment/setup on reader
      const intentId = paymentIntentId || setupIntentId;

      if (!intentId) {
        const errorData: ProcessPOSPaymentError = {
          code: "INVALID_INPUT",
          message: "Either paymentIntentId or setupIntentId must be provided",
          retryable: false,
        };
        const error = new Error(errorData.message);
        Object.assign(error, {
          ...errorData,
          actorName: "processPOSPayment",
          location: "process-pos-payment.actor.ts:223",
        });
        throw error;
      }

      const intentType = paymentIntentId ? "payment" : "setup";
      console.log(
        `[processPOSPaymentActor] Starting ${intentType} intent ${intentId} on reader ${readerId}`,
      );

      // Call the appropriate Stripe Terminal method based on intent type
      if (paymentIntentId) {
        await processPOSPaymentByReaderId(intentId, readerId, 10);
      } else {
        await processPOSSetupIntentByReaderId(intentId, readerId, 10);
      }

      // Step 2: Poll reader status until payment completes
      console.log(
        `[processPOSPaymentActor] Polling reader status every ${pollInterval}ms`,
      );
      const reader = await pollReaderStatus(
        readerId,
        pollInterval || 1000,
        timeout || 120000,
      );

      tracker.success();

      datadogLogs.logger.info("[Fleet] Payment succeeded", {
        action: "consumer_payment_succeeded",
        metadata: {
          kioskId: getKioskId(),
          stripeTerminalId: readerId,
          consumerId: consumerId ?? null,
          paymentIntentId: paymentIntentId ?? setupIntentId ?? null,
        },
      });

      // Set the new card as the customer's default payment method (fire-and-forget)
      if (setupIntentId) {
        fetch("/api/stripe/set-default-payment-method", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ setupIntentId }),
        }).catch((err) => {
          console.warn("*** Failed to set default payment method", err);
        });
      }

      const success: ProcessPOSPaymentSuccess = {
        status: "succeeded",
        paymentIntentId,
        setupIntentId,
        reader,
      };
      return success;
    } catch (error: unknown) {
      tracker.error(error);

      datadogLogs.logger.error("[Fleet] Payment failed", {
        action: "consumer_payment_failed",
        metadata: {
          kioskId: getKioskId(),
          stripeTerminalId: readerId,
          consumerId: consumerId ?? null,
          paymentIntentId: paymentIntentId ?? setupIntentId ?? null,
        },
      });
      // If it's already a ProcessPOSPaymentError, rethrow
      if ((error as ProcessPOSPaymentError).code) {
        throw error;
      }

      // Otherwise, wrap as unknown error
      const errorData: ProcessPOSPaymentError = {
        code: "UNKNOWN_ERROR",
        message:
          (error as Error).message || "Unknown error processing POS payment",
        retryable: true,
      };
      const err = new Error(errorData.message);
      Object.assign(err, {
        ...errorData,
        actorName: "processPOSPayment",
        location: "process-pos-payment.actor.ts:244",
      });
      throw err;
    }
  },
);

/**
 * Process POS Payment Actor with Metadata
 */
export const processPOSPaymentActor: ActorWithMetadata = {
  impl: processPOSPaymentActorImpl,
  metadata: {
    displayName: "processPOSPayment",
    description:
      "Process payment on Stripe Terminal Reader and poll for completion",
    category: "data",
    retryable: true,
    timeout: 120000, // 2 minutes
    errorCodes: [
      "INVALID_INPUT",
      "READER_OFFLINE",
      "READER_NOT_FOUND",
      "PAYMENT_FAILED",
      "PAYMENT_CANCELED",
      "TIMEOUT",
      "NETWORK_ERROR",
      "UNKNOWN_ERROR",
    ],
    inputSchema: ProcessPOSPaymentInputSchema,
    successSchema: ProcessPOSPaymentSuccessSchema,
    errorSchema: ProcessPOSPaymentErrorSchema,
    onSuccess: (result: unknown) => {
      const success = result as ProcessPOSPaymentSuccess;
      return {
        paymentStatus: "success",
        reader: success.reader,
        posError: undefined,
      };
    },
    onError: (error: unknown) => {
      const err = error as ProcessPOSPaymentError;
      return {
        paymentStatus: "failure",
        posError: {
          code: err.code,
          message: err.message,
          retryable: err.retryable,
        },
      };
    },
  },
};
